import React, { useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import ListPage from '@components/common/layout/ListPage';
import { DEFAULT_FORMAT, DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import useListBase from '@hooks/useListBase';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { BaseTable,BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { convertUtcToLocalTime, copyToClipboard } from '@utils';
import { Button } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const message = defineMessages({
    objectName: 'Request',
    copySuccess: { id: 'kitplayRequest.posIdCopied', defaultMessage: 'POS ID copied successfully!' },
    posId: { id: 'kitplayRequest.posId', defaultMessage: 'POS ID' },
});

const PiRequestTab = ({ isActive }) => {
    const translate = useTranslate();
    const notification = useNotification();

    const copyToClipboardAlert = () => {
        notification({ type: 'success', message: translate.formatMessage(message.copySuccess) });
    };
    const { data, mixinFuncs, queryFilter, loading, pagination, queryParams, serializeParams } = useListBase({
        apiConfig: apiConfig.kitplayRequest,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.additionalActionColumnButtons = () => {
                return {
                    copy: (record) => {
                        return (
                            <BaseTooltip title={'Copy'}>
                                <Button
                                    style={{ padding: 0 }}
                                    type="link"
                                    onClick={() => {
                                        copyToClipboard(record?.postId, translate);
                                    }}
                                >
                                    <CopyOutlined style={{ alignSelf: 'center' }} />
                                </Button>
                            </BaseTooltip>
                        );
                    },
                    deleteItem: ({ buttonProps, ...dataRow }) => {
                        return (
                            <Button
                                {...buttonProps}
                                type="link"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    mixinFuncs.showDeleteItemConfirm(dataRow.id);
                                }}
                                style={{ padding: 0 }}
                            >
                                <DeleteOutlined style={{ color: 'red' }} />
                            </Button>
                        );
                    },
                };
            };
        },
    });

    const columns = [
        {
            title: translate.formatMessage(message.posId),
            dataIndex: 'postId',
        },
        {
            title: translate.formatMessage(commonMessage.createdDate),
            dataIndex: 'requestTime',
            width: 200,
            render: (expireDate) => {
                const expireDateLocal = convertUtcToLocalTime(expireDate, DEFAULT_FORMAT, DEFAULT_FORMAT);
                return <div>{expireDateLocal}</div>;
            },
        },

        mixinFuncs.renderActionColumn(
            {
                copy: true,
                deleteItem: mixinFuncs.hasPermission([apiConfig.kitplayRequest.delete.baseURL]),
            },
            { width: '120px' },
        ),
    ];

    useEffect(() => {
        if (!isActive) return;
        mixinFuncs.getList();
        const interval = setInterval(() => {
            mixinFuncs.getList();
        }, 5000);

        return () => clearInterval(interval);
    }, [isActive]);

    return (
        <ListPage
            baseTable={
                <BaseTable
                    onChange={mixinFuncs.changePagination}
                    columns={columns}
                    dataSource={data}
                    pagination={pagination}
                />
            }
        />
    );
};

export default PiRequestTab;
